import { useCallback, useEffect, useRef, useState } from 'react';

import { Modal } from 'antd';

type HeaderColor = 'green' | 'orange' | 'red' | 'default';

interface Props {
    open: boolean;
    onClose: () => void;
    title?: string;
    children?: React.ReactNode;
    // Если true — шапка модального окна будет зелёной, иначе стандартная
    greenHeader?: boolean;
    // Цвет шапки модального окна
    headerColor?: HeaderColor;
}

const HEADER_COLORS: Record<HeaderColor, { background: string; color: string }> = {
    green: { background: '#2dbf64', color: '#fff' },
    orange: { background: '#ff8c00', color: '#fff' },
    red: { background: '#ff4d4f', color: '#fff' },
    default: { background: undefined, color: undefined },
};

export const DraggableModal: React.FC<Props> = ({
    open,
    onClose,
    title,
    children,
    greenHeader,
    headerColor,
}) => {
    const resolvedHeaderColor: HeaderColor = headerColor || (greenHeader ? 'green' : 'default');
    const headerStyles = HEADER_COLORS[resolvedHeaderColor];
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const modalRef = useRef<HTMLDivElement>(null);

    // Сброс позиции при открытии модального окна
    useEffect(() => {
        if (open) {
            setPosition({ x: 0, y: 0 });
        }
    }, [open]);

    // Обработчики для мыши (компьютер)
    const handleMouseDown = (e: React.MouseEvent) => {
        const target = e.target as HTMLElement;
        // Проверяем, что клик был по заголовку модального окна
        if (target.closest('.ant-modal-header')) {
            setIsDragging(true);
            setDragStart({
                x: e.clientX - position.x,
                y: e.clientY - position.y,
            });
        }
    };

    const handleMouseMove = useCallback(
        (e: MouseEvent) => {
            if (isDragging) {
                setPosition({
                    x: e.clientX - dragStart.x,
                    y: e.clientY - dragStart.y,
                });
            }
        },
        [isDragging, dragStart.x, dragStart.y]
    );

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    // Обработчики для касаний (мобильные устройства)
    const handleTouchStart = (e: React.TouchEvent) => {
        const target = e.target as HTMLElement;
        if (target.closest('.ant-modal-header')) {
            const touch = e.touches[0];
            setIsDragging(true);
            setDragStart({
                x: touch.clientX - position.x,
                y: touch.clientY - position.y,
            });
        }
    };

    const handleTouchMove = useCallback(
        (e: TouchEvent) => {
            if (isDragging && e.touches[0]) {
                const touch = e.touches[0];
                setPosition({
                    x: touch.clientX - dragStart.x,
                    y: touch.clientY - dragStart.y,
                });
            }
        },
        [isDragging, dragStart.x, dragStart.y]
    );

    const handleTouchEnd = useCallback(() => {
        setIsDragging(false);
    }, []);

    // Подписка на события перемещения и отпускания
    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.addEventListener('touchmove', handleTouchMove);
            document.addEventListener('touchend', handleTouchEnd);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', handleTouchEnd);
        };
    }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

    return (
        <Modal
            open={open}
            onCancel={onClose}
            title={title}
            footer={null}
            styles={{
                header:
                    resolvedHeaderColor !== 'default'
                        ? {
                              background: headerStyles.background,
                              color: headerStyles.color,
                              padding: '12px 16px',
                              borderBottom: 'none',
                          }
                        : undefined,
                content: {
                    padding: 0,
                },
                body: {
                    padding: '16px',
                },
            }}
            modalRender={(modal) => (
                <div
                    ref={modalRef}
                    onMouseDown={handleMouseDown}
                    onTouchStart={handleTouchStart}
                    style={{
                        transform: `translate(${position.x}px, ${position.y}px)`,
                        cursor: isDragging ? 'grabbing' : 'default',
                        touchAction: 'none',
                    }}
                >
                    {modal}
                </div>
            )}
            style={{
                pointerEvents: 'auto',
            }}
        >
            {children}
        </Modal>
    );
};
